import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Input, Space, Table, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { DailyMetrics, getDailyMetrics, LoginFunnelMetrics, LoginFunnelProvider } from '../monitor/trackingApi';
import { getTrackingApiBaseUrl } from '../../config/env';

interface MetricRow {
  key: string;
  date: string;
  family: string;
  metric: string;
  breakdown: string;
  value: unknown;
  percentageScale: number | null;
  unit?: string;
}

// Names from Tongits_Metrics & Dashboards 0.1 (Softlaunch), metric definitions.
const FAMILY_NAMES: Record<string, string> = {
  login: 'Phễu Login',
  inter_ads: 'Hành vi xem Inter Ads',
  rewarded_ads: 'Hành vi xem Rewarded Ads',
  shop_gold: 'Shop Gold',
  activity: 'Chỉ số tổng quát',
  tutorial: 'Funnel của Tutorial',
  easy_win: 'Easy Win',
  grace: 'Grace Period',
  betting: 'Bộ chỉ số theo mức cược',
  pay: 'Hành vi Pay',
  gameplay: 'Hành vi chơi game',
};
const METRIC_NAMES: Record<string, Record<string, string>> = {
  rewarded_ads: {
    a1: 'A1 — Số user active theo ngày chơi',
    users_offered: 'User thấy ít nhất 1 offer Rewarded',
    users_opted_in: 'User bấm xem Rewarded ít nhất 1 lần',
    views: 'Số Rewarded view đã xác nhận',
    clicks: 'Số lần bấm xem Rewarded',
    ready_clicks: 'Số lần bấm khi ad sẵn sàng',
    no_fill_clicks: 'Số lần bấm khi chưa có ads',
    unknown_state_clicks: 'Số lần bấm chưa rõ trạng thái ad',
    ready_rate_percent: 'Rewarded ready rate',
    no_fill_rate_percent: 'Rewarded no-fill rate',
    view_distribution: 'Phân bố số Rewarded trên mỗi user',
    placement_funnel: 'Funnel Rewarded theo placement',
  },
  inter_ads: {
    a1: 'A1 — Số user active theo ngày chơi',
    users_shown: 'User xem ít nhất 1 Inter',
    users_shown_percent: 'Tỷ lệ user xem Inter trên A1',
    impressions: 'Số Inter impression',
    impressions_per_a1: 'Inter impression trung bình trên A1',
    eligible_triggers: 'Số trigger đủ điều kiện',
    show_rate_percent: 'Inter show rate',
    avg_interval_seconds: 'Khoảng cách trung bình giữa hai Inter',
    impression_distribution: 'Phân bố số Inter trên mỗi user',
    placement_impressions: 'Số Inter theo placement',
    placement_percent: 'Tỷ lệ Inter theo placement',
  },
  activity: {
    a1: 'A1 — Số người chơi active trong ngày',
    n1: 'N1 — Số người chơi mới trong ngày',
    d1_retained: 'D1 Retained',
    d3_retained: 'D3 Retained',
    d7_retained: 'D7 Retained',
    rr1: 'RR1',
    rr3: 'RR3',
    rr7: 'RR7',
    cv0: 'CV0',
    cv1: 'CV1',
    cv3: 'CV3',
    cv7: 'CV7',
  },
  tutorial: {
    start: 'N1 Start Tutorial',
    knows_how_to_play: 'N1 Biết chơi',
    needs_tutorial: 'N1 Chưa biết chơi',
    finished: 'N1 Hoàn thành Tutorial',
    finished_play_now: 'N1 Hoàn thành Tutorial + Chơi ngay',
    finished_rulebook: 'N1 Hoàn thành Tutorial + Xem rule',
  },
  betting: {
    p1: 'P1 — Số người chơi trả tiền trong ngày',
    games: 'Số ván chơi theo mức cược',
    users: 'Số User theo mức cược',
    transactions: 'Trans theo mức cược',
    pay_users: 'Pay User theo mức cược',
    gross_revenue_vnd: 'Rev Gross theo mức cược (VND)',
    revenue_missing: 'Trans chưa có giá quy đổi doanh thu',
  },
  pay: {
    transactions: 'Số Trans (action) mua theo tính năng và giá mua',
    users: 'Số User mua theo tính năng và giá mua',
    gross_revenue_vnd: 'Số Rev Gross mua theo tính năng và giá mua (VND)',
    revenue_missing: 'Trans chưa có giá quy đổi doanh thu',
  },
  gameplay: {
    pvp_users: 'PVP User',
    bankrupt_by_count: 'Số User bị Bankrupt theo số lần',
    kicked_by_count: 'Số User bị Kick theo số lần (do không đủ tiền)',
  },
};
// Shop Gold funnel steps, named as in the dashboard spec.
const SHOP_STEP_NAMES: Record<string, string> = {
  shop_open: 'Vào Shop',
  offer_click: 'Bấm mua Offer',
  gold_click: 'Bấm mua gói Gold',
  gold_purchase: 'Mua thành công gói Gold',
};
const SHOP_METRIC_NAMES: Record<string, string> = {
  users: 'Số user',
  actions: 'Số action',
};
const CELL_FAMILIES = ['inter_ads', 'rewarded_ads', 'shop_gold'];
const BREAKDOWN_NAMES: Record<string, string> = {
  after_match: 'Sau ván chơi',
  daily_support: 'Daily Support',
  offer_shown: 'Offer shown',
  click: 'Click',
  ad_started: 'Ad started',
  ad_completed: 'Ad completed',
  reward_claimed: 'Reward claimed',
  all: 'Tất cả',
  google: 'Google',
  facebook: 'Facebook',
  apple: 'Apple',
  guest: 'Guest',
  pvp: 'PvP',
  bot_only: 'Chỉ chơi với Bot',
  unknown: 'Chưa xác định',
  starter_pack: 'Starter pack',
  piggy_bank: 'Piggy Bank',
  shop_gold: 'Shop Gold',
  remove_ads: 'Remove Ads',
  daily_offer: 'Daily Offer',
  double_deal: 'Double Deal',
  gold_pack: 'Gói Gold',
};

function metricName(family: string, path: string[]): { metric: string; breakdown: string } {
  let metric: string | undefined = METRIC_NAMES[family]?.[family === 'pay' ? path[path.length - 1] : path[0]];
  let breakdown = family === 'pay' ? path.slice(0, -1) : path.slice(1);
  if (family === 'easy_win') {
    metric = `Easy Win ${path[0]}`;
    breakdown = path.slice(1);
  } else if (family === 'grace') {
    metric = path[0] === 'pass' ? 'Grace Pass' : path[0] === 'games' ? `Grace ${path[1]}` : undefined;
    breakdown = path.slice(path[0] === 'games' ? 2 : 1);
  } else if (family === 'tutorial' && path[0] === 'steps') {
    metric = `N1 Chưa biết chơi + Step ${path[1]}`;
    breakdown = path.slice(2);
  }
  return {
    metric: metric ?? path.join(' / '),
    breakdown: breakdown.map((name) => BREAKDOWN_NAMES[name] ?? name).join(' / ') || '—',
  };
}

interface AdMetric {
  metric: string;
  dimensions: Record<string, string>;
  value: number | null;
}

function metricRows(days: DailyMetrics[]): MetricRow[] {
  const rows: MetricRow[] = [];
  for (const day of days) {
    for (const [family, metrics] of Object.entries(day.metrics)) {
      if (family === 'login') continue;
      if (CELL_FAMILIES.includes(family) && Array.isArray(metrics)) {
        const dimensionNames: Record<string, string> = {
          active_days: 'Ngày chơi',
          impressions: 'Số Inter',
          views: 'Số Rewarded',
          step: 'Bước',
          placement: 'Placement',
          product: 'Gói',
        };
        (metrics as AdMetric[]).forEach((cell, index) => {
          const isShop = family === 'shop_gold';
          const step = cell.dimensions.step;
          const metric = isShop
            ? `${SHOP_STEP_NAMES[step] ?? step} — ${SHOP_METRIC_NAMES[cell.metric] ?? cell.metric}`
            : METRIC_NAMES[family]?.[cell.metric] ?? cell.metric;

          rows.push({
            key: JSON.stringify([day.date, family, index]),
            date: day.date,
            family,
            metric,
            breakdown:
              Object.entries(cell.dimensions)
                .filter(([name]) => !(isShop && name === 'step'))
                .map(([name, value]) => `${dimensionNames[name] ?? name}: ${BREAKDOWN_NAMES[value] ?? value}`)
                .join(' / ') || '—',
            value: cell.value,
            percentageScale: cell.metric.endsWith('_percent') ? 1 : null,
            unit: cell.metric === 'avg_interval_seconds' ? ' s' : undefined,
          });
        });
        continue;
      }

      const visit = (value: unknown, path: string[]) => {
        if (family === 'activity' && path.length === 2 && path[1] !== 'all') return;
        if (value !== null && typeof value === 'object') {
          for (const [name, child] of Object.entries(value)) visit(child, [...path, name]);
        } else {
          rows.push({
            key: JSON.stringify([day.date, family, path]),
            date: day.date,
            family,
            ...metricName(family, path),
            value,
            percentageScale: family === 'activity' && ['rr1', 'rr3', 'rr7', 'cv0', 'cv1', 'cv3', 'cv7'].includes(path[0]) ? 100 : null,
          });
        }
      };
      visit(metrics, []);
    }
  }
  return rows;
}

export const MetricsPage: React.FC = () => {
  const date = new Date().toISOString().slice(0, 10);
  const [selectedDate, setSelectedDate] = useState(date);
  const trackingBaseUrl = getTrackingApiBaseUrl();
  const query = useQuery({
    queryKey: ['tracking-metrics', trackingBaseUrl, selectedDate],
    queryFn: ({ signal }) => getDailyMetrics(selectedDate, selectedDate, signal),
    enabled: Boolean(trackingBaseUrl && selectedDate),
    retry: false,
  });
  const rows = metricRows(query.data ?? []);
  const day = query.data?.find((item) => item.date === selectedDate);
  const login = day?.metrics.login as LoginFunnelMetrics | undefined;
  const loginRows = login && Array.isArray(login.providers) ? login.providers : [];
  const renderCount = (value: number | null | undefined) =>
    value == null ? '—' : new Intl.NumberFormat().format(value);

  return (
    <>
      <Typography.Title level={4}>Tracking metrics</Typography.Title>
      <Space className="mb-4" wrap align="end">
        <label>
          Date (UTC){' '}
          <Input
            type="date"
            aria-label="Metrics date"
            value={selectedDate}
            onChange={(event) => {
              if (event.target.value) setSelectedDate(event.target.value);
            }}
          />
        </label>
        <Button
          icon={<ReloadOutlined />}
          loading={query.isFetching}
          disabled={!trackingBaseUrl || !selectedDate}
          onClick={() => query.refetch()}
        >
          Refresh
        </Button>
      </Space>

      {!trackingBaseUrl ? (
        <Alert type="info" showIcon message="Tracking API is not configured" />
      ) : query.isError ? (
        <Alert
          type="error"
          showIcon
          message={query.error instanceof Error ? query.error.message : 'Failed to load tracking metrics'}
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      ) : (
        <>
          <div className="mt-3">
            <Typography.Title level={5}>Phễu Login</Typography.Title>
          
            {login && (
              <Typography.Paragraph>
                Login screen: {renderCount(login.new_user?.login_screen)} new-user actions ·{' '}
                {renderCount(login.active_user?.login_screen)} existing accounts
              </Typography.Paragraph>
            )}
            {login && !Array.isArray(login.providers) && (
              <Alert type="info" showIcon message="Provider breakdown is not yet available for this date." />
            )}
            <Table<LoginFunnelProvider>
              rowKey="provider"
              size="small"
              loading={query.isLoading}
              dataSource={loginRows}
              pagination={false}
              scroll={{ x: 1000 }}
              locale={{ emptyText: 'No login funnel for this date' }}
              columns={[
                {
                  title: 'Provider',
                  dataIndex: 'provider',
                  render: (provider: string) => BREAKDOWN_NAMES[provider] ?? provider,
                },
                { title: 'Click actions', dataIndex: 'click_actions', render: renderCount },
                { title: 'Unique installs clicked', dataIndex: 'unique_installs', render: renderCount },
                { title: 'New installs clicked', dataIndex: 'new_users', render: renderCount },
                { title: 'Existing accounts clicked', dataIndex: 'existing_users', render: renderCount },
                { title: 'Successful new accounts', dataIndex: 'success_new_users', render: renderCount },
                { title: 'Successful existing accounts', dataIndex: 'success_existing_users', render: renderCount },
              ]}
              summary={() => loginRows.length > 0 ? (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={5}>N1 — distinct successful new accounts</Table.Summary.Cell>
                  <Table.Summary.Cell index={5}>{renderCount(login?.n1)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={6}>—</Table.Summary.Cell>
                </Table.Summary.Row>
              ) : null}
            />
          </div>

          <div className="mt-6">
            <Typography.Title level={5}>Other metrics</Typography.Title>
            <Table<MetricRow>
              rowKey="key"
              size="small"
              loading={query.isLoading}
              dataSource={rows}
              scroll={{ x: 700 }}
              locale={{ emptyText: 'No tracking metrics for this date' }}
              pagination={false}
              columns={[
                {
                  title: 'Group',
                  dataIndex: 'family',
                  render: (family: string) => FAMILY_NAMES[family] ?? family,
                  filters: Array.from(new Set(rows.map((row) => row.family)))
                    .sort()
                    .map((family) => ({ text: FAMILY_NAMES[family] ?? family, value: family })),
                  onFilter: (value, row) => row.family === value,
                },
                { title: 'Metric', dataIndex: 'metric' },
                { title: 'Breakdown', dataIndex: 'breakdown' },
                {
                  title: 'Value',
                  dataIndex: 'value',
                  render: (value: unknown, row: MetricRow) =>
                    value == null
                      ? '—'
                      : row.percentageScale !== null && typeof value === 'number'
                        ? `${new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value * row.percentageScale)}%`
                        : CELL_FAMILIES.includes(row.family) && typeof value === 'number'
                          ? `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)}${row.unit ?? ''}`
                          : String(value),
                },
              ]}
            />

          </div>
        </>
      )}
    </>
  );
};
